import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, SlidersHorizontal, Search, MapPin, Check } from 'lucide-react';

const PRICE_PRESETS = [
  { value: 'all', label: 'Any price', min: 1, max: 1000 },
  { value: '0-500', label: 'Under $500', min: 1, max: 500 },
  { value: '500-1000', label: '$500 – $1,000', min: 500, max: 1000 },
  { value: '1000-2000', label: '$1,000 – $2,000', min: 1000, max: 2000 },
  { value: '2000+', label: '$2,000+', min: 2000, max: 100000 },
];

const RATING_PRESETS = [
  { value: 'all', label: 'Any' },
  { value: '4.5+', label: '4.5+' },
  { value: '4.0+', label: '4.0+' },
];

function OptionRow({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mfs-option ${active ? 'is-active' : ''}`}
    >
      <span className="mfs-option-label">{children}</span>
      <span className="mfs-option-check" aria-hidden>
        {active ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
      </span>
    </button>
  );
}

function Field({
  icon: Icon,
  label,
  prefix,
  type = 'text',
  ...props
}) {
  return (
    <label className="mfs-field">
      {label ? <span className="mfs-field-label">{label}</span> : null}
      <span className="mfs-field-shell">
        {Icon ? (
          <span className="mfs-field-icon" aria-hidden>
            <Icon className="h-[18px] w-[18px]" />
          </span>
        ) : null}
        {prefix ? <span className="mfs-field-prefix">{prefix}</span> : null}
        <input
          type={type}
          className={[
            'mfs-input',
            Icon ? 'has-icon' : '',
            prefix ? 'has-prefix' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...props}
        />
      </span>
    </label>
  );
}

const FilterBar = ({
  selectedPriceRange,
  setSelectedPriceRange,
  selectedRating,
  setSelectedRating,
  showMoreFilters,
  setShowMoreFilters,
  searchTerm = '',
  setSearchTerm,
  location = '',
  setLocation,
  selectedCategory = '',
  setSelectedCategory,
  categories = [],
  sortBy = 'none',
  setSortBy,
  sortOptions = [],
  clearAllFilters,
  showCategoryFilter = true,
  priceMin = 1,
  setPriceMin,
  priceMax = 1000,
  setPriceMax,
}) => {
  const hasCustomPrice =
    typeof setPriceMin === 'function' && typeof setPriceMax === 'function';

  const activeFiltersCount = [
    selectedPriceRange !== 'all',
    selectedRating !== 'all',
    Boolean(searchTerm?.trim?.()),
    Boolean(location?.trim?.()),
    sortBy && sortBy !== 'none',
    hasCustomPrice && (Number(priceMin) > 1 || Number(priceMax) < 1000),
    showCategoryFilter && Boolean(selectedCategory),
  ].filter(Boolean).length;

  useEffect(() => {
    if (!showMoreFilters) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setShowMoreFilters(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [showMoreFilters, setShowMoreFilters]);

  const handleClear = () => {
    if (clearAllFilters) {
      clearAllFilters();
      return;
    }
    setSelectedPriceRange?.('all');
    setSelectedRating?.('all');
    setSearchTerm?.('');
    setLocation?.('');
    setSortBy?.('none');
    setSelectedCategory?.('');
    if (hasCustomPrice) {
      setPriceMin(1);
      setPriceMax(1000);
    }
  };

  const applyPricePreset = (preset) => {
    setSelectedPriceRange?.(preset.value);
    if (hasCustomPrice) {
      setPriceMin(preset.min);
      setPriceMax(preset.max);
    }
  };

  const isPricePresetActive = (preset) => {
    if (!hasCustomPrice) return selectedPriceRange === preset.value;
    if (preset.value === 'all') {
      return Number(priceMin) <= 1 && Number(priceMax) >= 1000;
    }
    return (
      selectedPriceRange === preset.value ||
      (Number(priceMin) === preset.min && Number(priceMax) === preset.max)
    );
  };

  const sheet = showMoreFilters
    ? createPortal(
        <div className="marketplace-filter-sheet" data-marketplace-filters>
          <button
            type="button"
            className="mfs-backdrop"
            aria-label="Close filters"
            onClick={() => setShowMoreFilters(false)}
          />

          <div className="mfs-panel" role="dialog" aria-modal="true" aria-labelledby="mfs-title">
            <div className="mfs-grab" aria-hidden />

            <header className="mfs-header">
              <h2 id="mfs-title">Filters</h2>
              <button
                type="button"
                className="mfs-close"
                onClick={() => setShowMoreFilters(false)}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="mfs-body">
              {(typeof setSearchTerm === 'function' ||
                typeof setLocation === 'function') && (
                <section className="mfs-section">
                  <h3 className="mfs-section-title">Find</h3>
                  <div className="mfs-stack">
                    {typeof setSearchTerm === 'function' ? (
                      <Field
                        icon={Search}
                        type="search"
                        placeholder="Vendors or services"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoComplete="off"
                      />
                    ) : null}
                    {typeof setLocation === 'function' ? (
                      <Field
                        icon={MapPin}
                        type="text"
                        placeholder="City or area"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        autoComplete="off"
                      />
                    ) : null}
                  </div>
                </section>
              )}

              {showCategoryFilter &&
              categories.length > 0 &&
              typeof setSelectedCategory === 'function' ? (
                <section className="mfs-section">
                  <h3 className="mfs-section-title">Category</h3>
                  <div className="mfs-chips">
                    {categories.map((category) => {
                      const active = selectedCategory === category.value;
                      return (
                        <button
                          key={category.value}
                          type="button"
                          onClick={() =>
                            setSelectedCategory(active ? '' : category.value)
                          }
                          className={`mfs-chip ${active ? 'is-active' : ''}`}
                        >
                          {category.label}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="mfs-section">
                <h3 className="mfs-section-title">Price</h3>
                <div className="mfs-options">
                  {PRICE_PRESETS.map((preset) => (
                    <OptionRow
                      key={preset.value}
                      active={isPricePresetActive(preset)}
                      onClick={() => applyPricePreset(preset)}
                    >
                      {preset.label}
                    </OptionRow>
                  ))}
                </div>
                {hasCustomPrice ? (
                  <div className="mfs-price-range">
                    <Field
                      label="Min"
                      prefix="$"
                      type="number"
                      inputMode="numeric"
                      placeholder="1"
                      value={priceMin}
                      min="1"
                      onChange={(e) => {
                        const next = Math.max(
                          1,
                          parseInt(e.target.value, 10) || 1
                        );
                        setPriceMin(next);
                        setSelectedPriceRange?.('custom');
                      }}
                    />
                    <span className="mfs-price-dash" aria-hidden>
                      –
                    </span>
                    <Field
                      label="Max"
                      prefix="$"
                      type="number"
                      inputMode="numeric"
                      placeholder="1000"
                      value={priceMax}
                      min="1"
                      onChange={(e) => {
                        const next = Math.max(
                          1,
                          parseInt(e.target.value, 10) || 1000
                        );
                        setPriceMax(next);
                        setSelectedPriceRange?.('custom');
                      }}
                    />
                  </div>
                ) : null}
              </section>

              <section className="mfs-section">
                <h3 className="mfs-section-title">Rating</h3>
                <div className="mfs-segment">
                  {RATING_PRESETS.map((rating) => {
                    const active = selectedRating === rating.value;
                    return (
                      <button
                        key={rating.value}
                        type="button"
                        onClick={() => setSelectedRating(rating.value)}
                        className={`mfs-segment-btn ${active ? 'is-active' : ''}`}
                      >
                        {rating.value !== 'all' ? (
                          <Star className="h-3.5 w-3.5 fill-current" />
                        ) : null}
                        {rating.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {typeof setSortBy === 'function' && sortOptions.length > 0 ? (
                <section className="mfs-section mfs-section-last">
                  <h3 className="mfs-section-title">Sort by</h3>
                  <div className="mfs-options">
                    {sortOptions.map((opt) => (
                      <OptionRow
                        key={String(opt.value)}
                        active={String(sortBy) === String(opt.value)}
                        onClick={() => setSortBy(opt.value)}
                      >
                        {opt.label}
                      </OptionRow>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>

            <footer className="mfs-footer">
              <button type="button" className="mfs-btn mfs-btn-ghost" onClick={handleClear}>
                Clear
              </button>
              <button
                type="button"
                className="mfs-btn mfs-btn-solid"
                onClick={() => setShowMoreFilters(false)}
              >
                Show results
              </button>
            </footer>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowMoreFilters(true)}
        className={`mfs-trigger ${activeFiltersCount > 0 ? 'is-active' : ''}`}
      >
        <SlidersHorizontal className="h-4 w-4" />
        Filters
        {activeFiltersCount > 0 ? (
          <span className="mfs-trigger-count">{activeFiltersCount}</span>
        ) : null}
      </button>
      {sheet}
    </>
  );
};

export default FilterBar;
